/**
 * To-Do page — main view of all to-do items for the current user.
 * Shows job-linked and general items, filterable and completable.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchTodos, createTodo, toggleTodo, deleteTodo, updateTodo } from '../api/todos';
import client from '../api/client';
import { Button, LoadingSpinner, EmptyState, SearchableSelect } from '../components/common';
import { useAuth } from '../context/AuthContext';

function fmtDate(d) {
  if (!d) return '';
  try {
    return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return d; }
}

function fmtTimestamp(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return d; }
}

const PRIORITY_STYLES = {
  high: 'bg-red-100 text-red-700',
  normal: 'bg-gray-100 text-gray-600',
  low: 'bg-blue-100 text-blue-600',
};

export default function TodoPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active'); // 'active' | 'completed' | 'all'
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'job' | 'general'

  // Add form
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ title: '', description: '', priority: 'normal', due_date: '', job: '', assigned_to: '' });
  const [saving, setSaving] = useState(false);

  // Edit form
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  // Users and jobs for dropdowns
  const [allUsers, setAllUsers] = useState([]);
  const [allJobs, setAllJobs] = useState([]);

  const loadItems = useCallback(async () => {
    try {
      const params = { mine: true, page_size: 500 };
      if (filter === 'active') params.is_completed = false;
      else if (filter === 'completed') params.is_completed = true;
      if (typeFilter !== 'all') params.todo_type = typeFilter;
      const resp = await fetchTodos(params);
      setItems(resp.data || resp.results || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filter, typeFilter]);

  useEffect(() => { loadItems(); }, [loadItems]);

  // Load users and jobs for add form
  useEffect(() => {
    client.get('/auth/users/').then(({ data }) => {
      setAllUsers(data.data || data.results || data || []);
    }).catch(() => {});
    client.get('/jobs/', { params: { page_size: 500, ordering: '-job_number' } }).then(({ data }) => {
      setAllJobs(data.data || data.results || []);
    }).catch(() => {});
  }, []);

  async function handleToggle(id) {
    try {
      await toggleTodo(id);
      loadItems();
    } catch { /* ignore */ }
  }

  async function handleDelete(id) {
    if (!confirm('Delete this to-do item?')) return;
    try {
      await deleteTodo(id);
      loadItems();
    } catch { /* ignore */ }
  }

  function startEdit(item) {
    setEditingId(item.id);
    setEditForm({
      title: item.title || '',
      description: item.description || '',
      priority: item.priority || 'normal',
      due_date: item.due_date || '',
      assigned_to: item.assigned_to || '',
    });
  }

  async function handleSaveEdit() {
    if (!editForm.title.trim()) { alert('Title is required.'); return; }
    setEditSaving(true);
    try {
      await updateTodo(editingId, {
        title: editForm.title.trim(),
        description: editForm.description.trim(),
        priority: editForm.priority,
        due_date: editForm.due_date || null,
        assigned_to: editForm.assigned_to || undefined,
      });
      setEditingId(null);
      setEditForm({});
      loadItems();
    } catch (err) {
      alert(err.response?.data?.detail || err.response?.data?.error || 'Save failed');
    } finally {
      setEditSaving(false);
    }
  }

  async function handleAdd() {
    if (!addForm.title.trim()) { alert('Title is required.'); return; }
    setSaving(true);
    try {
      await createTodo({
        title: addForm.title.trim(),
        description: addForm.description.trim(),
        priority: addForm.priority,
        due_date: addForm.due_date || null,
        job: addForm.job || null,
        assigned_to: addForm.assigned_to || user.id,
      });
      setAddForm({ title: '', description: '', priority: 'normal', due_date: '', job: '', assigned_to: '' });
      setAddOpen(false);
      loadItems();
    } catch (err) {
      alert(err.response?.data?.error || JSON.stringify(err.response?.data) || 'Failed to create to-do');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="flex justify-center py-20"><LoadingSpinner /></div>;

  const activeCount = items.filter((i) => !i.is_completed).length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-foreground">To-Do List</h1>
        <Button size="sm" onClick={() => setAddOpen(!addOpen)}>
          {addOpen ? 'Cancel' : '+ New To-Do'}
        </Button>
      </div>

      {/* Add form */}
      {addOpen && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder="What needs to be done?"
              value={addForm.title}
              onChange={(e) => setAddForm({ ...addForm, title: e.target.value })}
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleAdd()}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description <span className="text-gray-400 font-normal">(optional)</span></label>
            <textarea
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              rows={2}
              placeholder="Additional details..."
              value={addForm.description}
              onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                value={addForm.priority}
                onChange={(e) => setAddForm({ ...addForm, priority: e.target.value })}
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                value={addForm.due_date}
                onChange={(e) => setAddForm({ ...addForm, due_date: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                value={addForm.assigned_to}
                onChange={(e) => setAddForm({ ...addForm, assigned_to: e.target.value })}
              >
                <option value="">Myself</option>
                {allUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Job <span className="text-gray-400 font-normal">(optional)</span></label>
              <SearchableSelect
                value={addForm.job}
                onChange={(v) => setAddForm({ ...addForm, job: v })}
                options={allJobs.map((j) => ({
                  value: j.id,
                  label: `#${j.job_number} — ${j.customer_name || j.parent_item_no || ''}`,
                }))}
                placeholder="None (general)"
                emptyMessage="No jobs match"
              />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button size="sm" loading={saving} onClick={handleAdd}>Add To-Do</Button>
            <Button size="sm" variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {['active', 'completed', 'all'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              filter === f ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f === 'active' ? `Active (${activeCount})` : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
        <span className="text-gray-300">|</span>
        {['all', 'job', 'general'].map((f) => (
          <button
            key={f}
            onClick={() => setTypeFilter(f)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              typeFilter === f ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f === 'all' ? 'All Types' : f === 'job' ? 'Job Tasks' : 'General'}
          </button>
        ))}
      </div>

      {/* Items list */}
      {items.length === 0 ? (
        <EmptyState
          title={filter === 'completed' ? 'No completed items' : 'No to-do items'}
          description={filter === 'active' ? 'Click "+ New To-Do" to add your first item.' : ''}
        />
      ) : (
        <div className="space-y-2">
          {items.map((item) => {
            const isOverdue = !item.is_completed && item.due_date && new Date(item.due_date + 'T23:59:59') < new Date();
            const isAssignedToMe = item.assigned_to === user?.id;
            const isCreatedByMe = item.created_by === user?.id;
            const isEditing = editingId === item.id;

            if (isEditing) {
              return (
                <div key={item.id} className="bg-white rounded-lg border border-blue-300 px-4 py-3 space-y-2 shadow-sm">
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-medium"
                    value={editForm.title}
                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                    autoFocus
                  />
                  <textarea
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    rows={2}
                    placeholder="Description (optional)"
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  />
                  <div className="flex items-end gap-3 flex-wrap">
                    <div>
                      <label className="block text-xs text-gray-500 mb-0.5">Priority</label>
                      <select className="border border-gray-300 rounded-md px-2 py-1.5 text-sm" value={editForm.priority} onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}>
                        <option value="low">Low</option>
                        <option value="normal">Normal</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-0.5">Due Date</label>
                      <input type="date" className="border border-gray-300 rounded-md px-2 py-1.5 text-sm" value={editForm.due_date} onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-0.5">Assign To</label>
                      <select className="border border-gray-300 rounded-md px-2 py-1.5 text-sm" value={editForm.assigned_to} onChange={(e) => setEditForm({ ...editForm, assigned_to: e.target.value })}>
                        <option value="">Myself</option>
                        {allUsers.map((u) => (
                          <option key={u.id} value={u.id}>{`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}</option>
                        ))}
                      </select>
                    </div>
                    <Button size="sm" loading={editSaving} onClick={handleSaveEdit}>Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                  </div>
                </div>
              );
            }

            return (
              <div
                key={item.id}
                className={`bg-white rounded-lg border px-4 py-3 flex items-start gap-3 transition-all ${
                  item.is_completed
                    ? 'border-gray-200 opacity-60'
                    : isOverdue
                    ? 'border-red-300 bg-red-50/30'
                    : 'border-gray-200 hover:border-blue-300 hover:shadow-sm'
                }`}
              >
                {/* Checkbox */}
                <button
                  onClick={() => handleToggle(item.id)}
                  className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    item.is_completed
                      ? 'bg-green-500 border-green-500 text-white'
                      : 'border-gray-300 hover:border-blue-500'
                  }`}
                >
                  {item.is_completed && (
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm font-medium ${item.is_completed ? 'line-through text-gray-400' : 'text-foreground'}`}>
                      {item.title}
                    </span>
                    {item.priority !== 'normal' && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium uppercase ${PRIORITY_STYLES[item.priority]}`}>
                        {item.priority}
                      </span>
                    )}
                    {item.due_date && (
                      <span className={`text-xs ${isOverdue ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
                        {isOverdue ? '⚠ ' : ''}Due {fmtDate(item.due_date)}
                      </span>
                    )}
                  </div>
                  {item.description && (
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{item.description}</p>
                  )}
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                    {item.job_number && (
                      <button
                        onClick={() => navigate(`/jobs/${item.job}`)}
                        className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                      >
                        Job #{item.job_number}{item.job_customer_name ? ` — ${item.job_customer_name}` : ''}
                      </button>
                    )}
                    {!item.job_number && (
                      <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-[10px] font-medium">General</span>
                    )}
                    {!isAssignedToMe && item.assigned_to_display && (
                      <span>→ {item.assigned_to_display.name}</span>
                    )}
                    {!isCreatedByMe && item.created_by_display && (
                      <span>from {item.created_by_display.name}</span>
                    )}
                    <span>{fmtTimestamp(item.created_at)}</span>
                    {item.is_completed && item.completed_by_display && (
                      <span className="text-green-600">✓ {item.completed_by_display.name} {fmtTimestamp(item.completed_at)}</span>
                    )}
                  </div>
                </div>

                {/* Edit + Delete buttons — only creator can edit, only active items */}
                {!item.is_completed && (
                  <div className="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
                    {isCreatedByMe && (
                      <button
                        onClick={() => startEdit(item)}
                        className="text-gray-300 hover:text-blue-500"
                        title="Edit"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="text-gray-300 hover:text-red-500"
                      title="Delete"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
